/**
 * Bundled taxonomy seeds for the Usage Categories page.
 *
 * `GENERAL_DEPARTMENT` is a fixed catch-all appended to a directory-generated
 * taxonomy so cross-cutting AI tasks (drafting an email, shortening text,
 * summarizing, casual follow-ups) have a real home instead of being force-fit
 * into a domain category. `DEFAULT_TAXONOMY` is a generic business taxonomy the
 * "Use default categories" action loads into the draft when a tenant would
 * rather not generate from their directory. Both are plain nested labels — the
 * view model turns them into draft nodes and the user reviews before saving.
 */

/** A label with optional children — the shape of a seeded taxonomy subtree. */
export interface SeedNode {
    label: string;
    children?: SeedNode[];
}

/** The fixed "General" catch-all: department → task group → specific task. */
export const GENERAL_DEPARTMENT: SeedNode = {
    label: 'General',
    children: [
        {
            label: 'Writing & Communication',
            children: [
                { label: 'Draft an email or message' },
                { label: 'Rewrite or shorten text' },
                { label: 'Improve tone or clarity of writing' },
                { label: 'Proofread and correct grammar' },
            ],
        },
        {
            label: 'Summarizing & Explaining',
            children: [
                { label: 'Summarize a document or conversation' },
                { label: 'Explain a concept in simple terms' },
                { label: 'Extract key points or action items' },
            ],
        },
        {
            label: 'General Assistance',
            children: [
                { label: 'Answer a general knowledge question' },
                { label: 'Brainstorm ideas or options' },
                { label: 'Research or look up a topic' },
            ],
        },
        {
            label: 'Conversation & Iteration',
            children: [
                { label: 'Refine or iterate on a previous response' },
                { label: 'Clarify or follow up on a request' },
                { label: 'Reformat or restructure content' },
            ],
        },
    ],
};

/** A generic, provider-agnostic business taxonomy for tenants who prefer not to
 * generate from their directory. Leads with the General catch-all, then common
 * functions. Reviewed and editable in the UI before saving. */
export const DEFAULT_TAXONOMY: SeedNode[] = [
    GENERAL_DEPARTMENT,
    {
        label: 'Sales',
        children: [
            {
                label: 'Prospecting & Outreach',
                children: [
                    { label: 'Draft a cold outreach email to a prospect' },
                    { label: 'Research a company before a sales call' },
                    { label: 'Write a follow-up message after a meeting' },
                    { label: 'Qualify a lead against ideal-customer criteria' },
                ],
            },
            {
                label: 'Deals & Proposals',
                children: [
                    { label: 'Draft a sales proposal or quote' },
                    { label: 'Summarize a deal status for the pipeline' },
                    { label: 'Respond to a pricing or discount request' },
                ],
            },
        ],
    },
    {
        label: 'Marketing',
        children: [
            {
                label: 'Content Creation',
                children: [
                    { label: 'Write a blog post or article draft' },
                    { label: 'Draft social media copy for a campaign' },
                    { label: 'Create marketing email copy' },
                    { label: 'Generate ad headlines and variations' },
                ],
            },
            {
                label: 'Campaign Analysis',
                children: [
                    { label: 'Summarize campaign performance metrics' },
                    { label: 'Analyze audience engagement trends' },
                    { label: 'Draft an A/B test plan' },
                ],
            },
        ],
    },
    {
        label: 'Customer Support',
        children: [
            {
                label: 'Ticket Handling',
                children: [
                    { label: 'Draft a reply to a customer support ticket' },
                    { label: 'Explain a product feature to a customer' },
                    { label: 'De-escalate a frustrated customer message' },
                    { label: 'Summarize a support conversation for handoff' },
                ],
            },
            {
                label: 'Knowledge & Documentation',
                children: [
                    { label: 'Write a help-center article' },
                    { label: 'Draft a troubleshooting guide' },
                    { label: 'Turn a resolved ticket into an FAQ entry' },
                ],
            },
        ],
    },
    {
        label: 'Finance & Accounting',
        children: [
            {
                label: 'Reporting & Analysis',
                children: [
                    { label: 'Summarize a financial report' },
                    { label: 'Explain a variance in the budget' },
                    { label: 'Build a formula for a spreadsheet calculation' },
                ],
            },
            {
                label: 'Transactions & Reconciliation',
                children: [
                    { label: 'Reconcile an invoice against a purchase order' },
                    { label: 'Draft a payment-reminder message' },
                    { label: 'Categorize a set of expenses' },
                ],
            },
        ],
    },
    {
        label: 'Human Resources',
        children: [
            {
                label: 'Hiring & Onboarding',
                children: [
                    { label: 'Write a job description for a role' },
                    { label: 'Draft interview questions for a candidate' },
                    { label: 'Create an onboarding checklist for a new hire' },
                ],
            },
            {
                label: 'People Operations',
                children: [
                    { label: 'Draft an internal policy announcement' },
                    { label: 'Summarize employee feedback from a survey' },
                    { label: 'Write a performance-review summary' },
                ],
            },
        ],
    },
    {
        label: 'Operations',
        children: [
            {
                label: 'Process & Documentation',
                children: [
                    { label: 'Document a standard operating procedure' },
                    { label: 'Draft a project status update' },
                    { label: 'Create a checklist for a recurring process' },
                ],
            },
            {
                label: 'Coordination',
                children: [
                    { label: 'Summarize meeting notes into action items' },
                    { label: 'Draft a vendor or supplier communication' },
                    { label: 'Plan a timeline for a project' },
                ],
            },
        ],
    },
    {
        label: 'IT & Engineering',
        children: [
            {
                label: 'Software Development',
                children: [
                    { label: 'Write or debug a code snippet' },
                    { label: 'Explain an error message or stack trace' },
                    { label: 'Draft or review technical documentation' },
                    { label: 'Design an approach for a technical task' },
                ],
            },
            {
                label: 'Support & Operations',
                children: [
                    { label: 'Troubleshoot a system or configuration issue' },
                    { label: 'Write a script to automate a task' },
                    { label: 'Summarize an incident and its resolution' },
                ],
            },
        ],
    },
];
